import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { ExternalLink } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NoteContent } from '@/components/NoteContent';
import { useAuthor } from '@/hooks/useAuthor';
import { formatRelativeTime } from '@/lib/time';
import { SITE } from '@/lib/site';

interface NoteCardProps {
  event: NostrEvent;
}

export function NoteCard({ event }: NoteCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.display_name || metadata?.name || SITE.name;
  const initials = displayName.split(' ').map((w) => w[0]).slice(0, 2).join('');

  const nevent = nip19.neventEncode({ id: event.id, author: event.pubkey });

  return (
    <article className="group relative rounded-xl border border-border bg-card p-5 transition-colors hover:border-bitcoin/30 sm:p-6">
      <div className="mb-3 flex items-center gap-3">
        <Avatar className="size-10 border border-border">
          <AvatarImage src={metadata?.picture} alt={displayName} />
          <AvatarFallback className="bg-gradient-to-br from-bitcoin to-nostr text-xs font-bold text-white">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-semibold">{displayName}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {formatRelativeTime(event.created_at)}
          </p>
        </div>

        <a
          href={`https://njump.me/${nevent}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open note in a Nostr client"
          className="rounded-full p-2 text-muted-foreground opacity-0 transition-all hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
        >
          <ExternalLink className="size-4" />
        </a>
      </div>

      <NoteContent event={event} className="text-[0.95rem] leading-relaxed" />
    </article>
  );
}
